import { useState, useEffect } from 'react';
import { XMarkIcon, FolderOpenIcon, TrashIcon } from '@heroicons/react/24/outline';
import { scopeService, type SavedScope } from '../../services/scopeService';
import type { Deliverable } from '@shared/types';
import toast from 'react-hot-toast';

interface LoadScopeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLoad: (deliverables: Deliverable[]) => void;
}

export function LoadScopeDialog({
  isOpen,
  onClose,
  onLoad,
}: LoadScopeDialogProps) {
  const [scopes, setScopes] = useState<SavedScope[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedScope, setSelectedScope] = useState<SavedScope | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadScopes();
    }
  }, [isOpen]);

  const loadScopes = async () => {
    setLoading(true);
    try {
      const data = await scopeService.getScopes();
      setScopes(data);
    } catch (error) {
      console.error('Failed to load scopes:', error);
      toast.error('Failed to load scopes');
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = () => {
    if (!selectedScope) {
      toast.error('Please select a scope');
      return;
    }

    onLoad(selectedScope.deliverables);
    toast.success(`Loaded scope: ${selectedScope.name}`);
    onClose();
    setSelectedScope(null);
  };

  const handleDelete = async (scope: SavedScope, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm(`Delete scope "${scope.name}"? This cannot be undone.`)) {
      return;
    }

    setDeleting(scope.id);
    try {
      await scopeService.deleteScope(scope.id);
      toast.success('Scope deleted');
      
      // Remove from list
      setScopes(scopes.filter(s => s.id !== scope.id));
      
      // Clear selection if deleted
      if (selectedScope?.id === scope.id) {
        setSelectedScope(null);
      }
    } catch (error) {
      console.error('Failed to delete scope:', error);
      toast.error('Failed to delete scope');
    } finally {
      setDeleting(null);
    }
  };

  const handleClose = () => {
    setSelectedScope(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
          onClick={handleClose}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-md">
                <FolderOpenIcon className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Load Saved Scope
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <p className="mt-2 text-sm text-gray-500">Loading scopes...</p>
              </div>
            ) : scopes.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No saved scopes yet</p>
                <p className="text-xs text-gray-400">Create deliverables and save them as a scope</p>
              </div>
            ) : (
              <div className="space-y-2">
                {scopes.map((scope) => (
                  <div
                    key={scope.id}
                    onClick={() => setSelectedScope(scope)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedScope?.id === scope.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{scope.name}</h3>
                        {scope.description && (
                          <p className="mt-1 text-sm text-gray-600">{scope.description}</p>
                        )}
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                          <span>{scope.deliverables.length} deliverable{scope.deliverables.length !== 1 ? 's' : ''}</span>
                          <span>•</span>
                          <span>Created {new Date(scope.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDelete(scope, e)}
                        disabled={deleting === scope.id}
                        className="ml-4 p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                        title="Delete scope"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleLoad}
              disabled={!selectedScope}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FolderOpenIcon className="w-4 h-4 mr-2" />
              Load Scope
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

